// Live's cookie consent (specs web/cookie-consent, web/cookie-consent-ask-here): the answer in the
// `ctv_consent` cookie, which live's landing site reads and writes too, live's region and GPC rules,
// our own marker of an answer given here, and Cookie settings' signal. The name, the shape, the
// attributes and the rules are the landing site's contract: change them there too, or not at all.
// The one module that touches `document.cookie`.

export const CONSENT_COOKIE = "ctv_consent";
/** The stored shape's version: a cookie of another version is no answer, and asks again. */
export const CONSENT_VERSION = 2;
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

/**
 * Where the answer was given, as the landing site reads it: opt-in asks first; opt-out (the US)
 * allows until told no. This app asks in both (spec web/cookie-consent-ask-here).
 */
export type ConsentMode = "opt-in" | "opt-out";
export type ConsentCategory = "analytics" | "advertising";
export type Consent = Record<ConsentCategory, boolean> & { mode: ConsentMode };

export const allowAll = (mode: ConsentMode): Consent => ({ analytics: true, advertising: true, mode });
export const denyAll = (mode: ConsentMode): Consent => ({ analytics: false, advertising: false, mode });

// The 50 states, DC and the territories, as live's list. A prefix test on America/ would take in
// Canada and Latin America, where consent comes first.
const US_TIME_ZONES = new Set([
  "America/New_York",
  "America/Detroit",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Indiana/Indianapolis",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Vevay",
  "America/Indiana/Tell_City",
  "America/Indiana/Knox",
  "America/Chicago",
  "America/Menominee",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/North_Dakota/Beulah",
  "America/Denver",
  "America/Boise",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Juneau",
  "America/Sitka",
  "America/Metlakatla",
  "America/Yakutat",
  "America/Nome",
  "America/Adak",
  "Pacific/Honolulu",
  "America/Puerto_Rico",
  "America/St_Thomas",
  "Pacific/Guam",
  "Pacific/Saipan",
  "Pacific/Pago_Pago",
]);

function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Opt-out in a US time zone; opt-in anywhere else, and when the zone cannot be read. A zone is not
 * a place (a VPN fools it), so a doubt asks, which costs a question rather than a consent.
 */
export function consentMode(timeZone: string | undefined = browserTimeZone()): ConsentMode {
  return timeZone !== undefined && US_TIME_ZONES.has(timeZone) ? "opt-out" : "opt-in";
}

/** Global Privacy Control, the browser's "do not sell or share": a decline until one is given. */
export function gpcOn(): boolean {
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

// Every value under the name: a host-only and a domain cookie can both be there, in no order a
// script can tell apart.
function storedValues(name: string = CONSENT_COOKIE): string[] {
  const prefix = `${name}=`;
  return document.cookie
    .split("; ")
    .filter((entry) => entry.startsWith(prefix))
    .map((entry) => entry.slice(prefix.length));
}

function parse(raw: string): Consent | null {
  try {
    const stored: unknown = JSON.parse(decodeURIComponent(raw));
    if (typeof stored !== "object" || stored === null) return null;
    const fields = stored as Record<string, unknown>;
    if (fields["v"] !== CONSENT_VERSION) return null;
    return {
      analytics: fields["analytics"] === true,
      advertising: fields["advertising"] === true,
      mode: fields["mode"] === "opt-out" ? "opt-out" : "opt-in",
    };
  } catch {
    return null;
  }
}

/** The stored answer: the first value of this version, or null when there is none. */
export function readConsent(): Consent | null {
  for (const raw of storedValues()) {
    const consent = parse(raw);
    if (consent !== null) return consent;
  }
  return null;
}

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const EPOCH = "Thu, 01 Jan 1970 00:00:00 GMT";

/**
 * `.` and the host's last two labels, which the landing site and every created.ai host share; null
 * on an IP or a one-label host, where a browser refuses a domain and would drop the whole cookie.
 */
export function cookieDomain(hostname: string): string | null {
  if (hostname.startsWith("[") || IPV4.test(hostname)) return null;
  const labels = hostname.split(".");
  return labels.length < 2 ? null : `.${labels.slice(-2).join(".")}`;
}

/** Stores the answer for 180 days, where the landing site reads it too. */
export function writeConsent(consent: Consent, where: Pick<Location, "hostname" | "protocol"> = window.location): void {
  const value = encodeURIComponent(JSON.stringify({ v: CONSENT_VERSION, ...consent }));
  const secure = where.protocol === "https:" ? "; Secure" : "";
  const cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  const domain = cookieDomain(where.hostname);
  if (domain !== null) {
    // A host-only copy left from before would shadow the shared one, so it goes first.
    document.cookie = `${CONSENT_COOKIE}=; path=/; expires=${EPOCH}`;
    document.cookie = `${cookie}; domain=${domain}`;
    if (storedValues().includes(value)) return;
  }
  // No domain, or one the browser refused (a public suffix, such as run.app): this host keeps it.
  document.cookie = cookie;
}

/**
 * Ours alone: an answer was given in this app, on this host. It is never on `.created.ai`, and
 * the landing site neither reads nor writes it, so its answer is no answer here.
 */
export const ANSWERED_COOKIE = "cai_consent_answered";
// Any other value is no marker, so raising it asks everyone once more (ask-here decision 4).
const ANSWERED = "1";

/** Whether this browser answered the banner here. */
export function answeredHere(): boolean {
  return storedValues(ANSWERED_COOKIE).includes(ANSWERED);
}

/** An answer given here: live's cookie, where the landing site reads it, then our marker, host-only. */
export function saveAnswer(consent: Consent, where: Pick<Location, "hostname" | "protocol"> = window.location): void {
  writeConsent(consent, where);
  const secure = where.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ANSWERED_COOKIE}=${ANSWERED}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

/**
 * Whether the banner asks: until an answer is given here, in every region, GPC or not, whatever
 * the landing site stored (Arturas, 2026-11-04), and again once that answer is gone.
 */
export function shouldAsk(): boolean {
  return !answeredHere() || readConsent() === null;
}

/**
 * Writes down the decline GPC implies, as live does on every load. Nothing else is implied: a US
 * time zone is asked like any other. An answer already stored stands.
 */
export function recordImpliedConsent(): void {
  if (readConsent() !== null) return;
  if (gpcOn()) writeConsent(denyAll(consentMode()));
}

const settingsListeners = new Set<() => void>();

/** Opens the banner on its categories, wherever it is mounted: the user menu's Cookie settings. */
export function openCookieSettings(): void {
  for (const listener of settingsListeners) listener();
}

/** Returns the unsubscribe, for an effect's cleanup. */
export function onCookieSettings(listener: () => void): () => void {
  settingsListeners.add(listener);
  return () => {
    settingsListeners.delete(listener);
  };
}
